import { mm, Viewer } from "@inovitas/infra3dsdk";
import { GeoFrame } from "@inovitas/infra3dsdk/dist/types/mm/geoframe/geoframe";
import { debounce } from "lodash";
import { Collection, Feature, Map, MapBrowserEvent, Overlay, View } from "ol";
import { Geometry, LineString, Point } from "ol/geom";
import { Modify, Snap } from "ol/interaction";
import BaseLayer from "ol/layer/Base";
import LayerGroup from "ol/layer/Group";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Fill, Icon, Stroke, Style, Text, Circle } from "ol/style";
import { FeatureLike } from "ol/Feature";
import { LinRefConfig } from "shared/common/utils/linref/addLinRefLayers";
import Layer from "ol/layer/Layer";
import { ModifyEvent } from "ol/interaction/Modify";

export default class AxisscaleBar {
  private _container: HTMLElement;
  private _viewer: Viewer;
  private _activeAxis: mm.Axis | null = null;

  private _km_stroke_dist = 100;
  private _stroke_label = 2;
  private _labelaccuracy = 1;
  private _ref_point_attribute: string = "";

  private _map: Map | null = null;

  private _actPosFeature: Feature<Point> | null = null;
  private _actPosSource: VectorSource | null = null;
  private _actPosLayer: VectorLayer<VectorSource> | null = null;

  private _lengthArray: number[] = [];
  private _panorotation: number = 0;
  private _actPosCoords: [number, number] = [0, 0];

  private _resizeObserver: ResizeObserver | null = null;
  private _geoframechangeevt: number | null = null;
  private _panorotationevt: number | null = null;

  private _jumps: Array<{ position: number; from: number; to: number }> = [];

  private _linRefConfig: LinRefConfig;

  // line, ticks on line, text on line
  private _axisStyle = {
    LineString: new Style({
      stroke: new Stroke({
        color: "white",
        width: 2,
        lineCap: "butt",
      }),
    }),
    Point: new Style({
      image: new Icon({
        src:
          "data:image/svg+xml;utf8," +
          '<svg width="5" height="34" viewBox="0 0 0.3 2" xmlns="http://www.w3.org/2000/svg">' +
          '<line x1="0.15" y1="0" x2="0.15" y2="2" stroke="white" stroke-width="0.2"/>' +
          "</svg>",
        scale: 0.3,
      }),
    }),
    PointLabel: new Style({
      text: new Text({
        font: "400 12px Roboto",
        rotation: 0,
        offsetY: -14,
        fill: new Fill({ color: "white" }),
      }),
    }),
    AxisPoint: new Style({
      image: new Circle({
        radius: 4,
        fill: new Fill({
          color: "white",
        }),
      }),
      text: new Text({
        font: "400 12px Roboto",
        rotation: 0,
        offsetY: -14,
        fill: new Fill({ color: "white" }),
      }),
    }),
  };

  private _actPosStyle = {
    default: new Style({
      image: new Icon({
        src: `data:image/svg+xml,<svg height="20" width="33" viewBox="0 0 33 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="m 49.4818,22 -19.5,14.7224 V 7.27757 Z" fill="%23FF6700" stroke="white" stroke-width="4"/>
            <circle cx="2.2" cy="22" r="20" fill="%23FF6700" stroke="white" stroke-width="4"/>
          </svg>`,
        scale: 0.8,
        anchor: [0.3, 0.5],
      }),
      zIndex: 100,
    }),
    hover: new Style({
      image: new Icon({
        src: `data:image/svg+xml,<svg height="20" width="33" viewBox="0 0 33 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="m 49.4818,22 -19.5,14.7224 V 7.27757 Z" fill="%23a64300" stroke="white" stroke-width="4"/>
            <circle cx="2.2" cy="22" r="20" fill="%23a64300" stroke="white" stroke-width="4"/>
          </svg>`,
        scale: 0.8,
        anchor: [0.3, 0.5],
      }),
      zIndex: 100,
    }),
  };

  constructor(
    viewer: Viewer,
    container: HTMLElement,
    linrefConfig: LinRefConfig
  ) {
    this._viewer = viewer;
    this._container = container;
    this._linRefConfig = linrefConfig;

    this._ref_point_attribute = linrefConfig.ref_point_attribute || "";

    this._initMap();

    this._subscribeEvents();
  }

  private _subscribeEvents = () => {
    if (!this._viewer) return;
    if (!this._map) return;
    this._calculateScalebarFeatures();

    const userInputHandler = this._viewer.getScene().userInputHandler;
    this._geoframechangeevt = userInputHandler.subscribeEvent(
      mm.USERINPUTTYPE.GEOFRAMECHANGED,
      (geoframe: GeoFrame) => {
        if (!this._viewer.getScene().getLRHandler().reloadingAxis) {
          this._movePointer(geoframe);
        }
      },
      undefined
    );

    this._panorotationevt = userInputHandler.subscribeEvent(
      mm.USERINPUTTYPE.PANO_ROTATION,
      (lat: number, lon: number) => {
        if (Math.abs(this._panorotation - lon) > 2) {
          this._panorotation = lon;
          this._updateViewingDirection();
        }
      },
      undefined
    );

    this._resizeObserver = new ResizeObserver((_) => {
      this._debouncedResize();
    });
    this._resizeObserver.observe(this._container);
  };

  private _unSubscribeEvents = () => {
    const userInputHandler = this._viewer.getScene().userInputHandler;
    if (this._geoframechangeevt) {
      userInputHandler.unSubscribeEvent(
        mm.USERINPUTTYPE.GEOFRAMECHANGED,
        this._geoframechangeevt
      );
    }
    if (this._panorotationevt) {
      userInputHandler.unSubscribeEvent(
        mm.USERINPUTTYPE.PANO_ROTATION,
        this._panorotationevt
      );
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  };

  /**
   * Calculates the needed data from the actual axis.
   */
  private _calculateScalebarFeatures = () => {
    if (
      !this._viewer ||
      !this._activeAxis ||
      !this._activeAxis.isnavigationref
    ) {
      this._clearDrawing();
      return;
    }
    this._jumps = [];

    const geoframe = this._viewer.getScene().getCurrentGeoframe();
    if (
      !this._activeAxis.xyz2uvw(
        geoframe.easting,
        geoframe.northing,
        geoframe.height
      )
    ) {
      this._clearDrawing();
      return;
    }

    this._lengthArray = [];
    for (const feature of this._activeAxis.axis?.features) {
      let len = 0;
      const coords: number[][] = feature.geometry.coordinates;
      for (let j = 0; j < coords.length - 1; j++) {
        len += Math.sqrt(
          Math.pow(coords[j + 1][0] - coords[j][0], 2) +
            Math.pow(coords[j + 1][1] - coords[j][1], 2)
        );
      }
      this._lengthArray.push(len);
    }

    // KM-Striche
    const coords: number[][] = [];
    if (this._km_stroke_dist) {
      let prevMaxU;
      for (let i = 0; i < this._activeAxis.axis?.features.length; i++) {
        const feature = this._activeAxis.axis?.features[i];
        const fp = feature.properties;

        const offsetAttr = this._activeAxis.offsetattribute;
        const lengthAttr = this._activeAxis.nominallengthattribute;

        // Get actual length from feature
        const lengthIst = fp[lengthAttr];

        // Get min_u (offset), defaulting to 0 if undefined
        const min_u = offsetAttr ? (fp[offsetAttr] ?? 0) : 0;

        // Get nominal length (length_soll), fallback to actual length if undefined
        const length_soll = fp[lengthAttr] ?? lengthIst;

        // Compute max_u
        const max_u = min_u + length_soll;

        for (
          let k = Math.ceil(min_u / this._km_stroke_dist);
          k <= Math.floor(max_u / this._km_stroke_dist);
          k++
        ) {
          const u_strich = k * this._km_stroke_dist;
          if (k !== 0)
            coords.push([this._activeAxis.u2absU(i, u_strich), 0, u_strich]);
        }

        if (this._linRefConfig.type === "rail-ch") {
          // set jump "Kilometersprung" indicators
          if (prevMaxU > min_u + 0.0002) {
            // Overlap
            // +/- 0.0002 fix for fp[offsetAttr] not being rounded properly?
            this._jumps.push({
              position: this._activeAxis.u2absU(i, min_u),
              from: prevMaxU,
              to: min_u,
            });
          }
          if (prevMaxU < min_u - 0.0002) {
            // Jump
            // +/- 0.0002 fix for fp[offsetAttr] not being rounded properly?
            this._jumps.push({
              position: this._activeAxis.u2absU(i, min_u),
              from: prevMaxU,
              to: min_u,
            });
          }
          prevMaxU = max_u;
        }
      }
    }

    this._recenterMap();
    this._calcFeatures(coords, this._lengthArray);

    this._movePointer(geoframe);
  };

  private _initMap = () => {
    this._map = new Map({
      target: this._container,
      layers: [],
      interactions: [],
      view: new View({
        center: [0, 0],
        resolution: 1.5,
      }),
      controls: [],
    });
  };

  private _clickOnLine = (evt: MapBrowserEvent<any>) => {
    this._movePosition(evt.coordinate[0]);
  };

  private _onPointerMove = (evt: MapBrowserEvent<any>) => {
    if (!this._map) return;
    const absU = evt.coordinate[0];
    const overlay = this._map.getOverlayById(1);

    if (absU < 0 || absU > this._lengthArray.reduce((a, b) => a + b)) {
      overlay?.setPosition(undefined);
      return;
    }
    this._map.getTargetElement().style.cursor = "pointer";
    overlay?.setPosition([absU, 0]);

    if (!this._activeAxis || !this._activeAxis.lrtransformer) return;
    const [u, featureidx] = this._activeAxis.absU2u(absU);

    let tooltip_text = "";
    if (this._ref_point_attribute) {
      const axisid_obj = this._activeAxis.getAxisIdentifier(featureidx);
      const axisid = Object.keys(axisid_obj)
        .map((x) => {
          return axisid_obj[x];
        })
        .slice(-1);
      tooltip_text = `${axisid} `;
    }

    let formatted_u = u.toFixed(0) + " m";
    if (u > 1000) {
      formatted_u = (u / 1000).toFixed(3) + " km";
    }
    const overlayElement = overlay?.getElement();
    if (overlayElement) overlayElement.innerHTML = tooltip_text + formatted_u;
  };

  private _addInteractions = () => {
    if (!this._map) return;
    // first remove all interactions
    this._removeInteractions();

    // add thumb-drag interaction
    if (this._actPosFeature && this._actPosSource && this._actPosLayer) {
      const modifyInteraction: Modify = new Modify({
        source: this._actPosSource,
        style: this._actPosStyle.hover,
        hitDetection: this._actPosLayer,
      });
      modifyInteraction.on("modifyend", (modifyEvt: ModifyEvent) => {
        this._movePosition(modifyEvt.mapBrowserEvent.coordinate[0]);
      });
      this._map.addInteraction(modifyInteraction);
      // add click interaction
      this._map.on("click", this._clickOnLine);
    }

    // add hover element/overlay
    const tooltip_element = document.createElement("div");
    tooltip_element.className = "infra3d-axisScalebar-tooltip";
    const overlay = new Overlay({
      id: 1,
      element: tooltip_element,
      autoPan: false,
      offset: [-40, -76],
      positioning: "bottom-center",
    });
    const old_overlay = this._map.getOverlayById(1);
    if (overlay && old_overlay) this._map.removeOverlay(old_overlay);
    this._map.addOverlay(overlay);

    // add hover interaction
    this._map.on("pointermove", this._onPointerMove);

    this._map.getTargetElement().onmouseout = () => {
      this._map?.getOverlayById(1)?.setPosition(undefined);
    };

    const axislinefeature: Feature<LineString | Point>[] = [
      new Feature<LineString>(
        new LineString([
          [0, 0],
          [this._lengthArray.reduce((a, b) => a + b), 0],
        ])
      ),
    ];
    const snapInteraction: Snap = new Snap({
      features: new Collection<Feature<Geometry>>([axislinefeature[0]]),
      pixelTolerance: 100000,
      edge: true,
      vertex: false,
    });
    this._map.addInteraction(snapInteraction);
  };

  private _removeInteractions = () => {
    if (!this._map) return;

    this._map
      .getInteractions()
      .getArray()
      .slice(0)
      .forEach((interaction) => {
        this._map?.removeInteraction(interaction);
      });
    this._map.un("click", this._clickOnLine);
    this._map.un("pointermove", this._onPointerMove);
    this._map.getTargetElement().style.cursor = "default";
    this._map.getTargetElement().onmouseout = () => {};
  };

  /**
   * Moves the pointer to the Actual position and checks, if the map has to be recentered. If so, it does a recenter.
   */
  private _movePointer = (geoframe: GeoFrame) => {
    if (!this._activeAxis || !this._map) return;
    const uvw = this._activeAxis.xyz2uvw(
      geoframe.easting,
      geoframe.northing,
      geoframe.height
    );

    if (!uvw) {
      return;
    }

    const absU = this._activeAxis.u2absU(uvw.featureidx, uvw.lrcoords[0]);
    this._actPosCoords = [absU, 0];
    if (!(
      geoframe.getMeasureInfo().streamtype === mm.STREAMTYPE.CALOTTE ||
      geoframe.getMeasureInfo().streamtype === mm.STREAMTYPE.CUBEMAP
    )) {
      this._panorotation = 0;
    } else {
      this._panorotation = geoframe.parentGeoFrameCollection.getLatLon()[1];
    }

    this._drawActualPos();

    // Recenter the map, if the pointer gets to the end.
    const currentextent: number[] = this._map
      .getView()
      .calculateExtent(this._map.getSize());
    const currentlength: number = currentextent[2] - currentextent[0];
    if (
      currentextent[0] + currentlength * 0.1 > this._actPosCoords[0] ||
      currentextent[2] - currentlength * 0.1 < this._actPosCoords[0]
    ) {
      this._recenterMap(this._actPosCoords[0]);
    }
  };

  private _updateViewingDirection = () => {
    if (!this._activeAxis) return;
    if (!this._activeAxis.lrtransformer) return;
    const geoframe = this._viewer.getScene().getCurrentGeoframe();
    if (!geoframe) return;

    let linear_orientation = Math.PI / 2;
    const azi = geoframe.getHorizontalOrientation() + this._panorotation;
    const future_easting =
      geoframe.easting + 1 * Math.sin((azi * Math.PI) / 180);
    const future_northing =
      geoframe.northing + 1 * Math.cos((azi * Math.PI) / 180);
    const future_uvw = this._activeAxis.xyz2uvw(
      future_easting,
      future_northing,
      geoframe.height
    );
    if (future_uvw) {
      const future_absolute_u = this._activeAxis.u2absU(
        future_uvw.featureidx,
        future_uvw.lrcoords[0]
      );
      const future_v = future_uvw.lrcoords[1];
      linear_orientation = Math.atan2(
        future_absolute_u - this._actPosCoords[0],
        -(future_v - this._actPosCoords[1])
      );
    } else {
      // If the future_uvw is not defined (mainly because it overlaps the axis), we calculate the back orientation
      const back_easting =
        geoframe.easting - 1 * Math.sin((azi * Math.PI) / 180);
      const back_northing =
        geoframe.northing - 1 * Math.cos((azi * Math.PI) / 180);
      const back_uvw = this._activeAxis.xyz2uvw(
        back_easting,
        back_northing,
        geoframe.height
      );

      if (back_uvw) {
        const back_absolute_u = this._activeAxis.u2absU(
          back_uvw.featureidx,
          back_uvw.lrcoords[0]
        );
        linear_orientation =
          (Math.atan2(
            back_absolute_u - this._actPosCoords[0],
            -(back_uvw.lrcoords[1] - this._actPosCoords[1])
          ) +
            Math.PI) %
          (2 * Math.PI);
      }
    }

    if (linear_orientation >= 0 && linear_orientation < Math.PI) {
      this._actPosStyle.default.getImage()?.setRotation(0);
      this._actPosStyle.hover.getImage()?.setRotation(0);
    } else {
      this._actPosStyle.default.getImage()?.setRotation(Math.PI);
      this._actPosStyle.hover.getImage()?.setRotation(Math.PI);
    }
    if (this._actPosFeature) this._actPosFeature.changed();
  };

  private _drawActualPos = () => {
    if (!this._map) return;

    if (this._actPosFeature) {
      this._actPosFeature
        .getGeometry()
        ?.setCoordinates([this._actPosCoords[0], 0]);
    } else {
      this._actPosFeature = new Feature<Point>({
        geometry: new Point([this._actPosCoords[0], 0]),
        name: "actposFeature",
      });
      this._actPosSource = new VectorSource({
        features: [this._actPosFeature],
      });
      this._actPosLayer = new VectorLayer({
        source: this._actPosSource,
        style: this._actPosStyle.default,
        zIndex: 100,
      });

      this._actPosLayer.setProperties({ name: "actposLayer" });

      this._map.addLayer(this._actPosLayer);

      this._addInteractions();
    }

    this._updateViewingDirection();
  };

  /**
   * Calculates the Feature Collection. The Feature Collection contains all elements except of the
   * pointer of the acutal position.
   */
  private _calcFeatures = (coords: number[][], length_array: number[]) => {
    const axislinefeature: Feature<LineString | Point>[] = [
      new Feature<LineString>({
        geometry: new LineString([
          [0, 0],
          [length_array.reduce((a, b) => a + b), 0],
        ]),
        name: "axisfeature",
      }),
    ];
    if (this._km_stroke_dist) {
      for (const coord of coords) {
        let label = "";
        if (coord[2] % (this._stroke_label * this._km_stroke_dist) == 0) {
          label = (coord[2] / 1000).toFixed(this._labelaccuracy);
        }
        axislinefeature.push(
          new Feature<Point>({
            geometry: new Point([coord[0], coord[1]]),
            label: label,
          })
        );
      }
    }

    const axispointfeature: Feature<Point>[] = [];
    if (this._ref_point_attribute && this._activeAxis) {
      let km_absolut_sum = 0;
      for (let i = 0; i < this._activeAxis.axis?.features.length; i++) {
        axispointfeature.push(
          new Feature<Point>({
            geometry: new Point([km_absolut_sum, 0]),
            name: "Axpoint",
            label:
              this._activeAxis.axis?.features[i].properties[
                this._ref_point_attribute
              ] ?? "",
          })
        );
        km_absolut_sum += length_array[i];
      }
    }

    // Update the Interactions
    this._addInteractions();

    this._addLayers(axislinefeature, axispointfeature);
  };

  /**
   * Calculates the VectorSource and VectorLayer of the linref features (except of the acutal position pointer).
   */
  private _addLayers = (
    axislinefeature: Feature<LineString | Point>[],
    axispointfeature: Feature<Point>[]
  ) => {
    if (!this._map) return;
    const layers: BaseLayer[] = this._map
      .getLayerGroup()
      .getLayers()
      .getArray();
    layers.forEach((layer) => {
      if (
        layer.getProperties().name !== "actposLayer" &&
        layer.getProperties().name !== "labelleft"
      ) {
        this._map?.removeLayer(layer);
      }
    });

    const layers_array: Layer[] = [];

    if (this._linRefConfig.type === "rail-ch") {
      const jumpFeatures: Feature<Point>[] = [];
      for (const jump of this._jumps) {
        jumpFeatures.push(
          new Feature<Point>({
            geometry: new Point([jump.position, 0]),
            label: `${(jump.from / 1000).toFixed(3)} → ${(jump.to / 1000).toFixed(3)}`,
            iconType: jump.from < jump.to ? "jump" : "overlap",
          })
        );
      }
      const jumpSource = new VectorSource({
        features: jumpFeatures,
      });

      const jumpLayer: VectorLayer<VectorSource> = new VectorLayer({
        source: jumpSource,
        style: (feature) => {
          const iconType = feature.get("iconType");
          let svg;
          switch (iconType) {
            case "jump":
              svg = `
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.5 13.5V20.5H8.5V18.5H3.5V15.5H8.5V13.5H11.5Z" fill="white" stroke="black" />
                <path d="M15.5 13.5V15.5H20.5V18.5H15.5V20.5H12.5V13.5H15.5Z" fill="white" stroke="black" />
                <path d="M11.8691 2.5L12.2646 2.50879L12.2773 2.50977L12.542 2.52637C15.1155 2.73207 17.3537 4.10203 18.7393 6.07617L20.833 5.33594L21.6426 5.0498L21.4922 5.89453L20.2158 13.0869L20.0693 13.9092L19.4141 13.3926L13.5137 8.74316L12.8018 8.18164L13.6562 7.87891L15.8193 7.11133C14.8824 6.10464 13.5725 5.45095 12.1367 5.37793L11.8594 5.37109V5.37207C8.87873 5.37564 6.56763 7.54358 6.46289 10.5352L6.44531 11.0332L5.94727 11.0176L3.98438 10.9551L3.48242 10.9395L3.5 10.4385C3.65725 5.94203 7.23555 2.50525 11.8564 2.5H11.8691Z" fill="white" stroke="black" />
              </svg>
            `;
              break;
            case "overlap":
              svg = `
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.5 15.5V22.5H13.5V20.5H3.5V17.5H13.5V15.5H16.5Z" fill="white" stroke="black"/>
                <path d="M10.5 10.5V12.5H20.5V15.5H10.5V17.5H7.5V10.5H10.5Z" fill="white" stroke="black"/>
                <path d="M12.21 2.5L11.8174 2.50879L11.8047 2.50977L11.5371 2.52734C8.93464 2.73855 6.67862 4.1576 5.31543 6.19336L3.19238 5.30859L2.40234 4.97949L2.50391 5.8291L3.36621 13.0596L3.46582 13.8955L4.15234 13.4072L10.2432 9.07617L10.9775 8.55371L10.1455 8.20703L8.08594 7.34668C9.02308 6.20103 10.4174 5.44974 11.9551 5.37402V5.37305L12.2256 5.36719C15.1684 5.37384 17.4531 7.52967 17.5566 10.5137L17.5742 11.0117L18.0723 10.9961L19.0439 10.9648H19.0449L20.0166 10.9336L20.5176 10.917L20.5 10.416C20.3441 5.93188 16.8012 2.50514 12.2217 2.5H12.21Z" fill="white" stroke="black"/>
              </svg>
              `;
              break;
            default:
              svg = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                </svg>
                `;
          }

          const font = "400 12px Roboto";

          const offsetX = this._calcPointLabelOffsetX(
            feature,
            font,
            feature.get("label")
          );
          if (offsetX === undefined) return; // Feature is outside the extent

          return new Style({
            image: new Icon({
              src: `data:image/svg+xml,${encodeURIComponent(svg.trim())}`,
              scale: 1,
              anchor: [0.5, 0.5],
            }),
            text: new Text({
              text: feature.get("label"),
              font: font,
              rotation: 0,
              offsetX: offsetX,
              offsetY: -14,
              fill: new Fill({ color: "white" }),
            }),
          });
        },
        declutter: true,
        zIndex: 3,
      });
      layers_array.push(jumpLayer);
    }

    const axislineSource: VectorSource = new VectorSource({
      features: axislinefeature,
    });

    const axislineLayer: VectorLayer<VectorSource> = new VectorLayer({
      source: axislineSource,
      style: (feature) => {
        if (feature.getGeometry()?.getType() === "Point") {
          return this._axisStyle.Point;
        } else {
          return this._axisStyle.LineString;
        }
      },
      zIndex: 1,
    });
    layers_array.push(axislineLayer);

    if (!this._ref_point_attribute) {
      const axislineLabelLayer: VectorLayer<VectorSource> = new VectorLayer({
        source: axislineSource,
        style: (feature) => {
          const style = this._axisStyle.PointLabel;
          const text = feature.getProperties().label?.toString();
          const offsetX = this._calcPointLabelOffsetX(
            feature,
            style.getText()?.getFont() || "400 12px Roboto",
            text
          );
          if (offsetX === undefined) return; // Feature is outside the extent

          style.getText()?.setText(text);
          style.getText()?.setOffsetX(offsetX);
          return style;
        },
        declutter: true,
        zIndex: 2,
      });
      layers_array.push(axislineLabelLayer);
    }

    if (this._ref_point_attribute) {
      const axispointSource: VectorSource = new VectorSource({
        features: new Collection(axispointfeature),
      });

      const axispointLayer: VectorLayer<VectorSource> = new VectorLayer({
        source: axispointSource,
        style: (feature) => {
          const style = this._axisStyle.AxisPoint;
          const text = feature.getProperties().label?.toString();
          const offsetX = this._calcPointLabelOffsetX(
            feature,
            style.getText()?.getFont() || "400 12px Roboto",
            text
          );
          if (offsetX === undefined) return; // Feature is outside the extent

          style.getText()?.setText(text);
          style.getText()?.setOffsetX(offsetX);
          return style;
        },
        declutter: true,
        zIndex: 2,
        className: "axispointLayer",
      });
      layers_array.push(axispointLayer);
    }

    const layergroup = new LayerGroup({
      layers: layers_array,
      zIndex: 0,
    });

    this._map.addLayer(layergroup);
  };

  private _calcPointLabelOffsetX = (
    feature: FeatureLike,
    font: string,
    text: string
  ) => {
    if (this._map === null) return;
    const geom = feature.getGeometry();
    if (geom instanceof Point) {
      const coords = geom.getCoordinates();
      const pixel = this._map.getPixelFromCoordinate(coords);
      const size = this._map.getSize();
      if (!size) return;
      const width = size[0];

      if (pixel[0] < 0 || pixel[0] > width) {
        return;
      }

      // Measure text width
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) return;
      context.font = font;
      const textWidth = context.measureText(text).width;

      let offsetX = 0;
      const dynamicMargin = textWidth / 2;

      if (pixel[0] < dynamicMargin) {
        offsetX = dynamicMargin - pixel[0];
      } else if (pixel[0] > width - dynamicMargin) {
        offsetX = width - dynamicMargin - pixel[0];
      }

      return offsetX;
    }
  };

  /**
   * Moves the position to the given u, v, and w values. Calculates the lon/lat and moves to the nearest geoframe.
   */
  private _movePosition = (u: number) => {
    if (!this._activeAxis || !this._viewer) return;
    const geoframe = this._viewer.getScene().getCurrentGeoframe();
    const uvw = this._activeAxis.xyz2uvw(
      geoframe.easting,
      geoframe.northing,
      geoframe.height
    );

    if (!uvw) {
      console.warn("Could not convert XYZ to UVW coordinates.");
      return;
    }

    let v = uvw.lrcoords[1];
    if (v < 0) {
      v -= 1;
    } else {
      v += 1;
    }

    const absU2u: number[] = this._activeAxis.absU2u(u);

    u = absU2u[0];
    const xyz = this._activeAxis.uvw2xyz(absU2u[1], u, v, 0);

    if (!xyz) {
      console.warn("Could not convert UVW to XYZ coordinates.");
      return;
    }

    let srid: number;
    if (this._activeAxis.axis?.crs.properties.code) {
      srid = this._activeAxis.axis?.crs.properties.code.toString();
    } else {
      srid = this._activeAxis.axis?.crs.properties.name.slice(5);
    }

    this._viewer
      .movePosition(
        {
          easting: xyz[0],
          northing: xyz[1],
        },
        srid
      )
      .then((node) => {
        // If the frame is not changed, update the pointer
        // as the geoframechanged event is not triggered
        if (geoframe.id === node.frame.id) {
          this._movePointer(geoframe);
        }
      })
      .catch((e) => {
        console.warn(e);
        this._movePointer(this._viewer.getScene().getCurrentGeoframe());
      });
  };

  private _clearDrawing = () => {
    if (!this._map) return;
    this._map
      .getLayers()
      .getArray()
      .slice(0)
      .forEach((layer) => this._map?.removeLayer(layer));
    this._actPosFeature = null;
    this._actPosSource = null;
    this._actPosLayer = null;
    this._removeInteractions();
  };

  private _debouncedResize = debounce(() => {
    this._resize();
  }, 100);

  private _resize = () => {
    if (!this._map) return;
    this._map.updateSize();
    this._calculateScalebarFeatures();
  };

  public changeAxis = (axis: mm.Axis | null) => {
    if (!this._viewer) return;
    this._activeAxis = axis;
    this._calculateScalebarFeatures();
  };

  public destroy = () => {
    this._clearDrawing();

    this._unSubscribeEvents();

    if (this._map) {
      const target = this._map.getTarget();
      if (!target || typeof target === "string") return;
      target.innerHTML = "";
    }
  };

  private _recenterMap = (x?: number) => {
    if (!this._viewer || !this._activeAxis || !this._map) return;
    if (!x) {
      const uvw = this._activeAxis.xyz2uvw(
        this._viewer.getScene().getCurrentGeoframe().easting,
        this._viewer.getScene().getCurrentGeoframe().northing,
        this._viewer.getScene().getCurrentGeoframe().height
      );

      if (!uvw) {
        return;
      }

      x = this._activeAxis.u2absU(uvw.featureidx, uvw.lrcoords[0]);
    }

    const current_resolution = this._map.getView().getResolution();
    if (!current_resolution) return;
    const map_size = this._map.getSize();
    if (!map_size) return;
    const panel_height_m = current_resolution * map_size[1];
    const centerOffsetY = 20;
    const y_offset_m = current_resolution * centerOffsetY;
    const view_width_m = current_resolution * map_size[0];
    const total_axis_length = this._lengthArray
      ? this._lengthArray.reduce((a, b) => a + b, 0)
      : 0;

    // Calculate the center position, handling edge cases
    let centerX = x;
    const paddingX = 20;
    const paddingX_m = current_resolution * paddingX;

    // If near the beginning of the axis
    if (x < view_width_m / 2) {
      centerX = view_width_m / 2 - paddingX_m;
    } else if (x > total_axis_length - view_width_m / 2) {
      // If near the end of the axis
      centerX = total_axis_length - view_width_m / 2 + paddingX_m;
    }

    this._map.getView().setCenter([centerX, panel_height_m / 2 - y_offset_m]);
  };
}
